// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import MenuPage from './pages/MenuPage';
import DeliveryPage from './pages/DeliveryPage';
import AdminPage from './pages/AdminPage';
import SuperAdminPage from './pages/SuperAdminPage';
import CourierPage from './pages/CourierPage';
import KitchenPage from './pages/KitchenPage';
import LoginPage from './pages/LoginPage';
import WaiterLoginPage from './pages/WaiterLoginPage';
import FrozenPage from './pages/FrozenPage';
import CourierPwaPage from './pages/CourierPwaPage';
import { usePageMeta } from './hooks/usePageMeta';
import { STAFF_PWA_COURIER, STAFF_PWA_KEY } from './lib/staffPwa';
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
/** KDS (KitchenPage) пишет отдельно; без очистки после logout /login остаётся «живой» кухонный кэш. */
const KITCHEN_TOKEN_KEY = 'kitchen_token';
const KITCHEN_USER_KEY = 'kitchen_user';

function parseUser(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearAuthStorage(storage) {
  storage.removeItem(TOKEN_KEY);
  storage.removeItem(USER_KEY);
}

function readMainAuth() {
  const localUser = parseUser(localStorage.getItem(USER_KEY));
  if (localUser?.role === 'kitchen') {
    // KDS-сессия не должна становиться глобальной сессией приложения.
    clearAuthStorage(localStorage);
  } else {
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken && localUser) return { token: localToken, user: localUser };
  }

  const sessionUser = parseUser(sessionStorage.getItem(USER_KEY));
  if (sessionUser?.role === 'kitchen') {
    return { token: null, user: null };
  }
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  return sessionToken && sessionUser
    ? { token: sessionToken, user: sessionUser }
    : { token: null, user: null };
}

function readStoredToken() {
  return readMainAuth().token;
}
function readStoredUser() {
  return readMainAuth().user;
}

const isSuperAdmin = (u) => u?.role === 'superadmin' || u?.is_super_admin;
const getHomeRoute = (u) => {
  if (isSuperAdmin(u)) return '/superadmin';
  if (u?.role === 'courier') return '/courier';
  if (u?.role === 'kitchen') return '/kitchen';
  return '/admin';
};

function AppRoutes({ token, user, onLogin, onLogout }) {
  usePageMeta(user);

  return (
    <Routes>
      {/* Статические маршруты ВСЕГДА выше динамических /:slug */}
      <Route path="/menu/:slug" element={<MenuPage />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/pwa-courier" element={<CourierPwaPage />} />

      <Route
        path="/waiter-login"
        element={
          token ? (
            <Navigate to={getHomeRoute(user)} replace />
          ) : (
            <WaiterLoginPage onLogin={onLogin} />
          )
        }
      />

      <Route
        path="/login"
        element={
          token ? (
            <Navigate to={getHomeRoute(user)} replace />
          ) : (
            <LoginPage onLogin={onLogin} />
          )
        }
      />

      <Route
        path="/superadmin"
        element={
          token && isSuperAdmin(user) ? (
            <SuperAdminPage
              token={token}
              user={user}
              onLogout={onLogout}
              onImpersonate={(newToken, newUser) => {
                onLogin(newToken, newUser);
                window.location.href = '/admin';
              }}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* /frozen ОБЯЗАТЕЛЬНО до /:slug */}
      <Route path="/frozen" element={<FrozenPage />} />

      <Route path="/delivery/:slug" element={<DeliveryPage />} />

      <Route
        path="/courier"
        element={
          token && (user?.role === 'courier' || user?.role === 'owner') ? (
            <CourierPage token={token} user={user} onLogout={onLogout} />
          ) : !token ? (
            <Navigate to="/login?hint=courier" replace />
          ) : (
            <Navigate to={getHomeRoute(user)} replace />
          )
        }
      />

      <Route path="/kitchen" element={<KitchenPage />} />

      <Route
        path="/admin"
        element={
          token &&
          (user?.role === 'owner' ||
            user?.role === 'waiter' ||
            user?.role === 'superadmin') ? (
            <AdminPage token={token} user={user} onLogout={onLogout} />
          ) : !token ? (
            // Нет сессии (закладка / PWA) — на вход персонала
            <Navigate to="/waiter-login" replace />
          ) : (
            // Кухня/курьер и т.д. в админ не пускаем — в «дом» роли, без /waiter-login (иначе мгновенно снова /kitchen|/courier)
            <Navigate to={getHomeRoute(user)} replace />
          )
        }
      />

      {/* Динамический /:slug — ПОСЛЕДНИЙ */}
      <Route path="/:slug" element={<MenuPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState(() => readStoredUser());

  // Миграция: старые сессии жили в sessionStorage — переносим в localStorage (PWA с «Домой» снова видит вход)
  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    const uRaw = sessionStorage.getItem(USER_KEY);
    const u = parseUser(uRaw);
    if (u?.role === 'kitchen') return;
    if (t && uRaw && !localStorage.getItem(TOKEN_KEY)) {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USER_KEY, uRaw);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    }
  }, []);

  const handleLogin = (newToken, userData) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(KITCHEN_TOKEN_KEY);
    sessionStorage.removeItem(KITCHEN_USER_KEY);
    localStorage.removeItem(KITCHEN_TOKEN_KEY);
    localStorage.removeItem(KITCHEN_USER_KEY);
    setToken(newToken);
    setUser(userData);
  };

  const handleLogout = () => {
    if (user?.role === 'courier') {
      try {
        localStorage.setItem(STAFF_PWA_KEY, STAFF_PWA_COURIER);
      } catch (_) { /* no-op */ }
    } else {
      try {
        localStorage.removeItem(STAFF_PWA_KEY);
      } catch (_) { /* no-op */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(KITCHEN_TOKEN_KEY);
    sessionStorage.removeItem(KITCHEN_USER_KEY);
    localStorage.removeItem(KITCHEN_TOKEN_KEY);
    localStorage.removeItem(KITCHEN_USER_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <BrowserRouter>
      <AppRoutes
        token={token}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
    </BrowserRouter>
  );
}
